// 9:16 portrait poster. The TikTok-shape primitive — every browse surface uses
// this and a horizontal-scrolling row of these stacked is the discovery feed.
//
// The card carries everything the mockup shows inside its frame: title in the
// active locale (bold), the alt-language title as a smaller subtitle, and a
// subtle "free first N episodes" line with a play-glyph. All sit on a bottom
// gradient (transparent top → deep ink bottom) so the photo reads clean while
// the text stays AA-contrasty on any image.

import { PlayCircle, Lock } from "lucide-react";
import { makeT } from "@bloom/i18n";
import { cn } from "../cn.ts";
import { Badge } from "./badge.tsx";

export interface VerticalPosterCardProps {
  href: string;
  titleTh: string;
  titleEn: string;
  /** Optional locale-paired taglines. The card renders the one matching `locale` as a subtitle. */
  taglineTh?: string | null;
  taglineEn?: string | null;
  posterUrl: string;
  rank?: number;
  locked?: boolean;
  locale?: "th" | "en";
  /** Show "First N episodes free" line. Omit to hide. */
  freeEpisodeCount?: number;
  className?: string;
  priority?: boolean;
}

export function VerticalPosterCard({
  href,
  titleTh,
  titleEn,
  taglineTh,
  taglineEn,
  posterUrl,
  rank,
  locked,
  locale = "en",
  freeEpisodeCount,
  className,
  priority,
}: VerticalPosterCardProps) {
  const t = makeT(locale);
  const title = locale === "th" ? titleTh : titleEn;
  const tagline = (locale === "th" ? taglineTh : taglineEn)?.trim();
  const showTagline = !!tagline && tagline !== title;

  return (
    <a
      href={href}
      className={cn(
        "group relative block aspect-poster overflow-hidden rounded-xl bg-[var(--brand-pink-soft)]",
        "transition-transform duration-160 ease-bloom-out motion-safe:hover:-translate-y-1 motion-safe:hover:scale-[1.03]",
        "active:scale-[0.98]",
        className,
      )}
    >
      <img
        src={posterUrl}
        alt={title}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-bloom-out group-hover:scale-105"
      />

      {/* Always-on bottom gradient. Transparent at the top half so the photo
          shows clean; deep ink at the bottom so the title block reads even on
          bright frames. Matches the mockup's tinted-bottom treatment. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 from-0% via-black/55 via-25% to-transparent to-60%"
      />

      {/* Hover wash — adds a faint extra tint over the whole card so the
          title block lifts on pointer-enter without obscuring the photo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-200 group-hover:bg-black/15"
      />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 space-y-0.5 p-3 sm:space-y-1 sm:p-4">
        <p className="font-heading line-clamp-1 text-sm font-extrabold leading-tight text-white drop-shadow-sm sm:text-base lg:text-lg">
          {title}
        </p>
        {showTagline ? (
          <p className="line-clamp-1 text-[11px] font-medium text-white/85 drop-shadow-sm sm:text-xs lg:text-sm">
            {tagline}
          </p>
        ) : null}
        {typeof freeEpisodeCount === "number" && freeEpisodeCount > 0 ? (
          <p className="flex items-center gap-1 pt-0.5 text-[10px] font-medium text-white/90 sm:gap-1.5 sm:pt-1 sm:text-xs lg:text-[13px]">
            <PlayCircle
              className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5"
              aria-hidden
              strokeWidth={2}
            />
            <span>{t("card.free_first_n", { count: freeEpisodeCount })}</span>
          </p>
        ) : null}
      </div>

      {typeof rank === "number" ? (
        <Badge variant="rank" className="absolute left-2 top-2">
          TOP {rank}
        </Badge>
      ) : null}
      {locked ? (
        <span
          aria-label={t("card.locked")}
          className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white"
        >
          <Lock className="h-3.5 w-3.5" aria-hidden />
        </span>
      ) : null}
    </a>
  );
}
